import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Product, Sector, Stock, StockApi, StockBase } from '@app/core/interfaces';
import {EbayService, ProductService, SectorService, StockService, UtilityService} from '@app/core/services';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RatingModule } from 'primeng/rating';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {EbayProductAnalysis} from "@app/core/interfaces/ebay";

@Component({
  selector: 'app-product-stock-form',
  imports: [
    CommonModule,
    FormsModule,
    RadioButtonModule,
    InputTextModule,
    ButtonModule,
    FloatLabelModule,
    MessageModule,
    TagModule,
    RatingModule,
    InputNumberModule,
    SelectModule,
    InputMaskModule,
    TextareaModule,
    ProgressSpinnerModule,
    ToggleSwitchModule
  ],
  templateUrl: './product-stock-form.html'
})
export class ProductStockForm implements OnDestroy, OnInit {
  @Input() product!: Product;
  @Input() stock: Partial<StockBase | Stock> = {
    etat: null,
    note: null,
    prix: null,
    quantite: null,
    etagere: null,
    rayon: null,
    commentaire: null,
    ebay: null,
    url_ebay: null,
    secteur_id: null,
    produit_id: null
  };
  @Input() recommendedPrice!: EbayProductAnalysis;

  private originalStock: Partial<StockBase | Stock> = {};
  protected apiCall$?: Subscription;
  private destroy$: Subject<any> = new Subject();

  isLoadingData: boolean = true;
  isRecommendedPriceLoaded: boolean = false;
  isSendingDatas: boolean = false;

  secteurs: Sector[] = [];

  constructor(
    private dialogRef: DynamicDialogRef,
    private stockService: StockService,
    private sectorService: SectorService,
    private productService: ProductService,
    private ebayService: EbayService,
    public utilityService: UtilityService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    // Store a deep copy of the original stock values to detect changes later
    this.originalStock = JSON.parse(JSON.stringify(this.stock));
    this.getProductRecommendedPrice()
    this.getSectors()
  }

  getSectors(): void {
    this.isLoadingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.sectorService
      .getCollection()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.secteurs = data.results,
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingData = false;
        }
      });
  }

    getProductRecommendedPrice(): void {
        this.isRecommendedPriceLoaded = false;
        this.ebayService
            .getEbayAnalyseMarche(this.product.id as string | number)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => this.recommendedPrice = data,
                complete: () => this.isRecommendedPriceLoaded = true
            });
    }

  hideDialog(): void {
    this.isSendingDatas = false;
    this.dialogRef.close(this.stock.id ? this.stock : null);
  }

  private getChangedFields(): Partial<StockBase | Stock> {
    const changes: Partial<StockBase | Stock> = {};
    const keys = Object.keys(this.stock) as Array<keyof (StockBase | Stock)>;

    for (const key of keys) {
      if (JSON.stringify(this.stock[key]) !== JSON.stringify(this.originalStock[key])) {
        changes[key] = this.stock[key];
      }
    }

    return changes;
  }

  public hasChanges(): boolean {
    // If creating a new stock (no id), always allow saving
    if (!this.stock.id) {
      return true;
    }
    // If editing, check if there are any changes
    const changes = this.getChangedFields();
    return Object.keys(changes).length > 0;
  }

  saveStock(): void {
    this.isSendingDatas = true;
    this.stock.produit_id = this.product.id;

    let observable: Observable<StockApi>;
    let messageOption: ToastMessageOptions;
    let isUpdateEbayStocks: boolean = false;
    let dataToSend: any = this.stock;

    if (this.stock.id) {
      // For PATCH: send only the changed fields
      dataToSend = this.getChangedFields();
      dataToSend.id = this.stock.id;
      observable = this.stockService.patch(dataToSend.id, dataToSend);
      messageOption = { severity: 'info', summary: `Modification d'un stock`, detail:  `La modification du stock a été complété avec succès!` };
    } else {
      observable = this.stockService.post(dataToSend);
      messageOption = { severity: 'info', summary: `Création d'un stock`, detail: `La création du stock a été complété avec succès!` };
      isUpdateEbayStocks = true;
    }

    this.apiCall$?.unsubscribe();
    this.apiCall$ = observable
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: StockApi) => this.stock = response.result,
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.messageService.add(messageOption);
          if (isUpdateEbayStocks && this.product.produits_ebay && this.product.produits_ebay.length > 0) {
            this.updateEbayStocks();
          } else {
            this.isSendingDatas = false;
            this.hideDialog();
          }
        }
      });
  }

  updateEbayStocks(): void {
    this.isSendingDatas = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService.postGenererEbay(this.product.id, { update_only: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          // this.messageService.add({ severity: 'success', summary: 'Mise à jour', detail: 'Mise à jour des stocks Ebay effectuée.' });
          this.isSendingDatas = false;
          this.hideDialog();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.destroy$) {
      this.destroy$.next(true);
      this.destroy$.complete();
    }
    if(this.apiCall$) {
      this.apiCall$.unsubscribe();
    }
  }
}
